// Address autocomplete and country-specific form utilities

// Google Places API configuration
let googleMapsLoaded = false;
let autocompleteService = null;
let placesService = null;

// Load Google Maps API - DISABLED to avoid API errors
export const loadGoogleMapsAPI = () => {
  return Promise.reject(new Error('Google Maps API disabled - using simple address input'));
};

// Initialize Google Places services
const initializeServices = () => {
  if (window.google && window.google.maps) {
    autocompleteService = new window.google.maps.places.AutocompleteService();
    // Create a dummy map for PlacesService (required by Google)
    const map = new window.google.maps.Map(document.createElement('div'));
    placesService = new window.google.maps.places.PlacesService(map);
  }
};

// Get address predictions
export const getAddressPredictions = (input, countryCode = 'US') => {
  return new Promise((resolve, reject) => {
    if (!autocompleteService) {
      reject(new Error('Google Places service not initialized'));
      return;
    }

    const request = {
      input: input,
      types: ['address'],
      componentRestrictions: { country: countryCode.toLowerCase() }
    };

    autocompleteService.getPlacePredictions(request, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        resolve(predictions || []);
      } else {
        reject(new Error(`Places service error: ${status}`));
      }
    });
  });
};

// Get detailed place information
export const getPlaceDetails = (placeId) => {
  return new Promise((resolve, reject) => {
    if (!placesService) {
      reject(new Error('Google Places service not initialized'));
      return;
    }

    const request = {
      placeId: placeId,
      fields: [
        'address_components',
        'formatted_address',
        'geometry',
        'name'
      ]
    };

    placesService.getDetails(request, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        resolve(place);
      } else {
        reject(new Error(`Place details error: ${status}`));
      }
    });
  });
};

// Parse address components from Google Places
export const parseAddressComponents = (addressComponents, countryCode) => {
  const components = {
    streetNumber: '',
    streetName: '',
    address1: '',
    city: '',
    state: '',
    postal: '',
    country: countryCode
  };

  addressComponents.forEach(component => {
    const types = component.types;
    
    if (types.includes('street_number')) {
      components.streetNumber = component.long_name;
    }
    if (types.includes('route')) {
      components.streetName = component.long_name;
    }
    if (types.includes('locality')) {
      components.city = component.long_name;
    }
    if (types.includes('administrative_area_level_1')) {
      components.state = component.short_name;
    }
    if (types.includes('postal_code')) {
      components.postal = component.long_name;
    }
    if (types.includes('country')) {
      components.country = component.short_name;
    }
  });

  // Combine street number and name
  components.address1 = `${components.streetNumber} ${components.streetName}`.trim();

  return components;
};

// Country-specific form field configurations
export const COUNTRY_FORM_CONFIG = {
  US: {
    postalLabel: 'ZIP Code',
    postalPattern: /^\d{5}(-\d{4})?$/,
    stateLabel: 'State',
    stateRequired: true,
    postalRequired: true,
    phoneFormat: '+1 (XXX) XXX-XXXX',
    phonePattern: /^\+1\s\(\d{1,3}\)\s\d{0,3}-?\d{0,4}$/,
    addressFormat: ['address1', 'address2', 'city', 'state', 'postal'],
    stateOptions: ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']
  },
  CA: {
    postalLabel: 'Postal Code',
    postalPattern: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
    stateLabel: 'Province',
    stateRequired: true,
    postalRequired: true,
    phoneFormat: '+1 (XXX) XXX-XXXX',
    phonePattern: /^\+1\s\(\d{1,3}\)\s\d{0,3}-?\d{0,4}$/,
    addressFormat: ['address1', 'address2', 'city', 'state', 'postal'],
    stateOptions: ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT']
  },
  GB: {
    postalLabel: 'Postcode',
    postalPattern: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
    stateLabel: 'County',
    stateRequired: false,
    postalRequired: true,
    phoneFormat: '+44 XXXX XXXXXX',
    phonePattern: /^\+44\s\d{4}\s\d{6}$/,
    addressFormat: ['address1', 'address2', 'city', 'postal']
  },
  FR: {
    postalLabel: 'Code postal',
    postalPattern: /^\d{5}$/,
    stateLabel: 'Région',
    stateRequired: false,
    postalRequired: true,
    phoneFormat: '+33 X XX XX XX XX',
    phonePattern: /^\+33\s\d?\s?\d{0,2}\s?\d{0,2}\s?\d{0,2}\s?\d{0,2}$/,
    addressFormat: ['address1', 'address2', 'postal', 'city']
  },
  DE: {
    postalLabel: 'Postleitzahl',
    postalPattern: /^\d{5}$/,
    stateLabel: 'Bundesland',
    stateRequired: false,
    postalRequired: true,
    phoneFormat: '+49 XXX XXXXXXXX',
    phonePattern: /^\+49\s\d{3}\s\d{8}$/,
    addressFormat: ['address1', 'address2', 'postal', 'city']
  },
  ES: {
    postalLabel: 'Código postal',
    postalPattern: /^\d{5}$/,
    stateLabel: 'Provincia',
    stateRequired: false,
    postalRequired: true,
    phoneFormat: '+34 XXX XXX XXX',
    phonePattern: /^\+34\s\d{3}\s\d{3}\s\d{3}$/,
    addressFormat: ['address1', 'address2', 'postal', 'city', 'state']
  },
  AU: {
    postalLabel: 'Postcode',
    postalPattern: /^\d{4}$/,
    stateLabel: 'State/Territory',
    stateRequired: true,
    postalRequired: true,
    phoneFormat: '+61 XXX XXX XXX',
    phonePattern: /^\+61\s\d{3}\s\d{3}\s\d{3}$/,
    addressFormat: ['address1', 'address2', 'city', 'state', 'postal'],
    stateOptions: ['ACT','NSW','NT','QLD','SA','TAS','VIC','WA']
  },
  MA: {
    postalLabel: 'Code postal',
    postalPattern: /^\d{5}$/,
    stateLabel: 'Région',
    stateRequired: false,
    postalRequired: false,
    phoneFormat: '+212 XXX-XXXXXX',
    phonePattern: /^\+212\s\d{3}-\d{6}$/,
    addressFormat: ['address1', 'address2', 'city', 'state', 'postal']
  },
  SA: {
    postalLabel: 'Postal Code',
    postalPattern: /^\d{5}$/,
    stateLabel: 'Province',
    stateRequired: false,
    postalRequired: false,
    phoneFormat: '+966 XX XXX XXXX',
    phonePattern: /^\+966\s\d{2}\s\d{3}\s\d{4}$/,
    addressFormat: ['address1', 'address2', 'city', 'postal']
  },
  AE: {
    postalLabel: 'P.O. Box',
    postalPattern: /^.+$/,
    stateLabel: 'Emirate',
    stateRequired: true,
    postalRequired: false,
    phoneFormat: '+971 XX XXX XXXX',
    phonePattern: /^\+971\s\d{2}\s\d{3}\s\d{4}$/,
    addressFormat: ['address1', 'address2', 'city', 'state'],
    stateOptions: ['Abu Dhabi','Dubai','Sharjah','Ajman','Umm Al Quwain','Ras Al Khaimah','Fujairah']
  },
// Default configuration (Fallback)
  DEFAULT: {
    postalLabel: 'Postal Code',
    postalPattern: /^.+$/,
    stateLabel: 'State/Province',
    stateRequired: false,
    postalRequired: false,
    phoneFormat: '+XXX XXXXXXXXX',
    phonePattern: /^\+\d{1,4}\s.+$/,
    addressFormat: ['address1', 'address2', 'city', 'state', 'postal']
  }
};

// Global Address Engine: Get country-specific configuration dynamically
export const getCountryConfig = (countryCode) => {
  const code = (countryCode || 'US').toUpperCase();
  
  // Return explicit config if defined above
  if (COUNTRY_FORM_CONFIG[code]) return COUNTRY_FORM_CONFIG[code];

  // Dynamic grouping for the rest of the 240+ countries
  
  // 1. EU & Eastern Europe (ZIP -> City, state usually not required)
  const zipCity = [
    'IT','NL','BE','CH','AT','SE','NO','DK','FI','PL','CZ',
    'HU','PT','GR','TR','IL','RU','UA','RO','BG','HR','RS','SK','SI','EE','LV','LT'
  ];
  
  // 2. Commonwealth & specific format (City -> ZIP, no state)
  const cityZip = ['ZA', 'NZ', 'SG', 'IE', 'KE', 'NG'];
  
  // 3. Middle East / No Zip (City -> State, no ZIP)
  const cityState = ['QA', 'BH', 'OM', 'KW', 'LB', 'JO', 'IQ', 'YE'];
  
  // 4. City Only (No state, no zip needed)
  const cityOnly = ['HK', 'MO', 'JM', 'FJ', 'BS', 'BZ'];

  const defaultConfig = COUNTRY_FORM_CONFIG.DEFAULT;

  if (zipCity.includes(code)) {
    return { ...defaultConfig, addressFormat: ['address1', 'address2', 'postal', 'city'], postalRequired: true, stateRequired: false };
  }
  if (cityZip.includes(code)) {
    return { ...defaultConfig, addressFormat: ['address1', 'address2', 'city', 'postal'], postalRequired: true, stateRequired: false };
  }
  if (cityState.includes(code)) {
    return { ...defaultConfig, addressFormat: ['address1', 'address2', 'city', 'state'], postalRequired: false, stateRequired: true };
  }
  if (cityOnly.includes(code)) {
    return { ...defaultConfig, addressFormat: ['address1', 'address2', 'city'], postalRequired: false, stateRequired: false };
  }

  // 5. Rest of the world (City, State/Region, ZIP)
  return defaultConfig;
};

// Phone number formatting utilities
export const formatPhoneNumber = (phone, countryCode) => {
  const config = getCountryConfig(countryCode);
  const digitsOnly = phone.replace(/\D/g, '');
  
  switch (countryCode) {
    case 'US':
    case 'CA':
      if (digitsOnly.length === 10) {
        return `+1 (${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
      }
      break;
    case 'GB':
      if (digitsOnly.length === 11 && digitsOnly.startsWith('44')) {
        const number = digitsOnly.slice(2);
        return `+44 ${number.slice(0, 4)} ${number.slice(4)}`;
      }
      break;
    case 'FR':
      if (digitsOnly.length === 10) {
        return `+33 ${digitsOnly.charAt(0)} ${digitsOnly.slice(1, 3)} ${digitsOnly.slice(3, 5)} ${digitsOnly.slice(5, 7)} ${digitsOnly.slice(7)}`;
      }
      break;
    case 'DE':
      if (digitsOnly.length >= 10) {
        return `+49 ${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
      }
      break;
    case 'ES':
      if (digitsOnly.length === 9) {
        return `+34 ${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6)}`;
      }
      break;
    case 'AU':
      if (digitsOnly.length === 9) {
        return `+61 ${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6)}`;
      }
      break;
    case 'MA':
      if (digitsOnly.length === 9) {
        return `+212 ${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
      }
      break;
  }
  
  return phone; // Return original if no formatting rule matches
};

// Validate phone number
export const validatePhoneNumber = (phone, countryCode) => {
  const config = getCountryConfig(countryCode);
  return config.phonePattern.test(phone);
};

// Validate postal code
export const validatePostalCode = (postal, countryCode) => {
  const config = getCountryConfig(countryCode);
  // Normalize input
  const value = (postal || '').toString().trim();

  // Allow empty values to avoid blocking submission; server-side can enforce if needed
  if (value.length === 0) {
    return true;
  }

  // Country-specific strict pattern first
  if (config.postalPattern && config.postalPattern.test(value)) {
    return true;
  }

  // Fallback: permissive pattern that accepts common formats across countries
  // - Letters, digits, spaces, and hyphens
  // - Length between 2 and 12 characters
  const permissivePattern = /^[A-Za-z0-9 \-]{2,12}$/;
  if (permissivePattern.test(value)) {
    return true;
  }

  // As a final fallback, accept anything 2–16 chars (do not block checkout)
  return value.length >= 2 && value.length <= 16;
};

// Get phone country code
export const getPhoneCountryCode = (countryCode) => {
  const phoneCodes = {
    US: '+1',
    CA: '+1',
    GB: '+44',
    FR: '+33',
    DE: '+49',
    ES: '+34',
    AU: '+61',
    MA: '+212',
    MX: '+52',
    BR: '+55',
    JP: '+81',
    KR: '+82',
    CN: '+86',
    IN: '+91',
    RU: '+7',
    IT: '+39',
    NL: '+31',
    BE: '+32',
    CH: '+41',
    AT: '+43',
    SE: '+46',
    NO: '+47',
    DK: '+45',
    FI: '+358',
    PL: '+48',
    CZ: '+420',
    HU: '+36',
    PT: '+351',
    GR: '+30',
    TR: '+90',
    IL: '+972',
    SA: '+966',
    AE: '+971',
    EG: '+20',
    ZA: '+27',
    NG: '+234',
    KE: '+254',
    GH: '+233',
    TN: '+216',
    DZ: '+213',
    LY: '+218',
    SN: '+221',
    CI: '+225',
    ML: '+223',
    BF: '+226'
  };
  
  return phoneCodes[countryCode] || '+1';
}; 